import * as XLSX from "xlsx";

import { formatIDR } from "@/lib/format";
import type { RateRow } from "@/lib/rate-api";
import {
  computeCutiAdjustedTotalsForRateRow,
  incentiveTotalFromRate,
  taxTotalFromRate,
  safeDetailsProject,
  safePaidLeavePayload,
  safeReimbursePayload,
} from "@/lib/rate-row-helpers";

function countWeekdaysInclusive(start: Date, end: Date): number {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  let n = 0;
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (d <= last) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
}

function workingDaysLabel(periodStartYmd: string | null, periodEndYmd: string | null): string {
  if (!periodStartYmd || !periodEndYmd) return "-";
  const a = new Date(`${periodStartYmd}T00:00:00`);
  const b = new Date(`${periodEndYmd}T00:00:00`);
  const n = countWeekdaysInclusive(a, b);
  return n > 0 ? `${n} hari` : "-";
}

/** Format detik jadi pola seperti contoh AllEarn.xlsx: "160 jam 13 menit". */
function secondsToJamMenitId(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const jam = Math.floor(s / 3600);
  const menit = Math.floor((s % 3600) / 60);
  if (jam === 0 && menit === 0) return "0 jam";
  if (jam === 0) return `${menit} menit`;
  if (menit === 0) return `${jam} jam`;
  return `${jam} jam ${menit} menit`;
}

function sanitizeFileBasename(name: string): string {
  const t = name.replace(/[/\\?%*:|"<>]/g, " ").replace(/\s+/g, " ").trim();
  return t.length ? t : "periode";
}

function sanitizeSheetName(name: string): string {
  return name
    .replace(/[:\\/?*[\]]/g, "-")
    .trim()
    .slice(0, 31);
}

function reimbAmountFromRow(r: RateRow): number {
  const reimb = safeReimbursePayload(r.reimburse ?? null);
  if (!reimb) return 0;
  if (typeof reimb.total_reimburse === "number" && Number.isFinite(reimb.total_reimburse)) return reimb.total_reimburse;
  return (reimb.details_reimburse ?? []).reduce(
    (acc, d) => acc + (typeof d?.reimburse === "number" && Number.isFinite(d.reimburse) ? d.reimburse : 0),
    0,
  );
}

function buildDataRow(r: RateRow, hariKerjaLabel: string): string[] {
  const details = safeDetailsProject(r.details_project);
  const totals = details.totals || {};
  const cutiAdj = computeCutiAdjustedTotalsForRateRow(r);
  const addSeconds = cutiAdj.adjustedAddSeconds;
  const baseSeconds = cutiAdj.adjustedLoggedSeconds;
  const combinedSeconds = baseSeconds + addSeconds;
  const baseEarn = cutiAdj.effectiveBaseEarn;
  const addEarn = cutiAdj.adjustedAddEarn;
  const reimbAmount = reimbAmountFromRow(r);
  const incAmount = incentiveTotalFromRate(r.incentive);
  const taxAmount = taxTotalFromRate(r.tax);
  const paidLeave = safePaidLeavePayload(r.paid_leave ?? null);
  const paidLeaveAmount =
    paidLeave && typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours) ? paidLeave.total_hours * r.rate_hours : 0;
  const grandEarn =
    baseEarn == null && addEarn == null && reimbAmount === 0 && incAmount === 0 && paidLeaveAmount === 0 && taxAmount === 0
      ? null
      : (baseEarn ?? 0) + (addEarn ?? 0) + reimbAmount + incAmount + paidLeaveAmount - taxAmount;

  const rowActivity =
    cutiAdj.adjustedActivityPct != null
      ? cutiAdj.adjustedActivityPct
      : typeof totals.activityPct === "number" && Number.isFinite(totals.activityPct)
        ? totals.activityPct
        : typeof r.total_activity === "number" && Number.isFinite(r.total_activity)
          ? Math.min(100, Math.max(0, r.total_activity))
          : null;
  const activityDisplay = rowActivity == null ? "0%" : `${Math.round(rowActivity)}%`;

  const gaji = baseEarn ?? 0;
  const additionalStr = incAmount > 0 ? formatIDR(incAmount) : "-";
  const reimburseStr = reimbAmount > 0 ? formatIDR(reimbAmount) : "-";
  const totalNum = grandEarn ?? 0;

  return [
    r.name_employee.trim(),
    hariKerjaLabel,
    secondsToJamMenitId(combinedSeconds),
    activityDisplay,
    formatIDR(gaji),
    additionalStr,
    reimburseStr,
    formatIDR(totalNum),
  ];
}

/**
 * Ekspor ringkasan pendapatan semua baris rate periode ke .xlsx,
 * struktur header mengikuti referensi `backend/src/reference/AllEarn.xlsx`.
 */
export function downloadAllEarnStyleXlsx(opts: {
  rates: RateRow[];
  /** Nama file tanpa ekstensi, mis. "mei 2026" → disanitasi jadi aman untuk filesystem. */
  fileBasename: string;
  /** Nama sheet (maks. 31 karakter aturan Excel). */
  sheetName: string;
  periodStartDate: string | null;
  periodEndDate: string | null;
}): void {
  const hariKerjaLabel = workingDaysLabel(opts.periodStartDate, opts.periodEndDate);
  const sorted = [...opts.rates].sort((a, b) =>
    a.name_employee.trim().localeCompare(b.name_employee.trim(), "id", { sensitivity: "base" }),
  );

  const rows: string[][] = [];
  rows.push(["Nama", "Hari Kerja Aktif", "Hivedesk", "", "", "Additional Living", "Reimburse", "Total"]);
  rows.push(["", "", "Jam Kerja", "Activity", "Gaji", "", "", ""]);
  for (const r of sorted) {
    rows.push(buildDataRow(r, hariKerjaLabel));
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!merges"] = [{ s: { r: 0, c: 2 }, e: { r: 0, c: 4 } }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(opts.sheetName));

  const fname = `${sanitizeFileBasename(opts.fileBasename)}.xlsx`;
  XLSX.writeFile(wb, fname);
}
